import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import TurnstileWidget from '../components/TurnstileWidget';
import { useBranding } from '../branding';
import { Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TURNSTILE_SITE_NTOA = process.env.REACT_APP_TURNSTILE_SITE_NTOA;

/**
 * Insured-Facing Portal — Login + Register + Accept Invite + Magic Link.
 * Renders the appropriate form based on the URL:
 *   /insured/login           → password login
 *   /insured/register        → organic self-signup (NTOA "Join Now" flow)
 *   /insured/accept-invite?token=...  → set password from invite token
 *   /insured/magic-link?token=... → passwordless link consume
 */
const InsuredAuth = ({ mode = 'login' }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const branding = useBranding();
  const [loading, setLoading] = useState(false);
  const [magicLoading, setMagicLoading] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', name: '' });
  const [turnstileToken, setTurnstileToken] = useState('');
  const token = searchParams.get('token');

  const isAccept = mode === 'accept-invite';
  const isMagic = mode === 'magic-link';
  const isRegister = mode === 'register';
  const isForgot = mode === 'forgot-password';
  const isReset = mode === 'reset-password';
  const consumeRanRef = useRef(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [resetDone, setResetDone] = useState(false);

  // Magic-link consume flow runs once on mount when mode==='magic-link'
  useEffect(() => {
    if (!isMagic) return;
    if (!token) {
      toast.error('Missing or invalid sign-in link.');
      return;
    }
    // Guard against React.StrictMode double-mount (and any future re-render
    // that would otherwise re-fire the one-time consume call).
    if (consumeRanRef.current) return;
    consumeRanRef.current = true;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/insured/magic-link/consume`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          throw new Error(err.detail || 'Failed');
        }
        toast.success('Welcome back!');
        navigate('/insured');
      } catch (e) {
        toast.error(e.message || 'Sign-in failed');
        navigate('/insured/login');
      }
    })();
  }, [isMagic, token, navigate]);

  const requestMagicLink = async () => {
    if (!form.email) {
      toast.error('Enter your email first.');
      return;
    }
    setMagicLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/insured/magic-link/request`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.email }),
      });
      if (!r.ok) throw new Error('Failed');
      toast.success('Check your email — a sign-in link is on the way.');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setMagicLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Register + forgot-password both need a Turnstile token (when configured).
    if ((isRegister || isForgot) && TURNSTILE_SITE_NTOA && !turnstileToken) {
      toast.error('Please complete the verification below.');
      return;
    }
    setLoading(true);
    try {
      let url;
      let body;
      if (isRegister) {
        url = `${API_URL}/api/insured/register`;
        body = {
          email: form.email,
          password: form.password,
          name: form.name,
          turnstile_token: turnstileToken || null,
        };
      } else if (isForgot) {
        url = `${API_URL}/api/insured/forgot-password`;
        body = { email: form.email, turnstile_token: turnstileToken || null };
      } else if (isReset) {
        url = `${API_URL}/api/insured/reset-password`;
        body = { token, new_password: form.password };
      } else if (isAccept) {
        url = `${API_URL}/api/insured/accept-invite`;
        body = { token, password: form.password, name: form.name };
      } else {
        url = `${API_URL}/api/insured/login`;
        body = { email: form.email, password: form.password };
      }
      const res = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed');
      }
      if (isRegister) {
        toast.success('Welcome to NTOA! Your account is ready.');
        navigate('/insured');
      } else if (isForgot) {
        setForgotSent(true);
      } else if (isReset) {
        setResetDone(true);
        toast.success('Password updated — you\u2019re signed in.');
        // Backend sets the session cookie already; give the user a moment
        // to see the success state, then drop them into the portal.
        setTimeout(() => navigate('/insured'), 1200);
      } else if (isAccept) {
        toast.success('Account created.');
        navigate('/insured');
      } else {
        toast.success('Welcome back.');
        navigate('/insured');
      }
    } catch (err) {
      toast.error(err.message || 'Failed');
    } finally {
      setLoading(false);
    }
  };

  if (isMagic) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4"
        style={{
          background:
            'linear-gradient(135deg, rgba(30,58,138,0.95), rgba(15,23,42,0.95))',
        }}
      >
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle>Signing you in…</CardTitle>
            <p className="text-sm text-gray-500">
              Validating your sign-in link.
            </p>
          </CardHeader>
          <CardContent className="text-center py-6">
            <Loader2 className="h-8 w-8 mx-auto animate-spin text-blue-600" />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Forgot-password success state — replaces the form once the request
  // has been sent. Anti-enumeration copy: never reveal whether the
  // email is actually on file.
  if (isForgot && forgotSent) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4"
        style={{
          background:
            'linear-gradient(135deg, rgba(30,58,138,0.95), rgba(15,23,42,0.95))',
        }}
      >
        <Card className="w-full max-w-md" data-testid="insured-forgot-sent">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <ShieldCheck className="h-7 w-7 text-blue-600" />
              <span className="text-xl font-bold">
                {branding?.display_name || 'Customer Portal'}
              </span>
            </div>
            <CardTitle className="text-lg">Check your email</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-600 text-center">
              If that email is on file, we&apos;ve sent you a link to reset
              your password. The link is good for one hour.
            </p>
            <p className="text-xs text-gray-500 text-center">
              Didn&apos;t get it? Check your spam folder, or try the
              &ldquo;Email me a sign-in link&rdquo; option on the sign-in
              page.
            </p>
            <div className="text-center">
              <Link
                to="/insured/login"
                className="text-sm text-blue-600 hover:underline"
                data-testid="insured-forgot-back-to-login"
              >
                ← Back to sign in
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{
        background:
          'linear-gradient(135deg, rgba(30,58,138,0.95), rgba(15,23,42,0.95))',
      }}
    >
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <ShieldCheck className="h-7 w-7 text-blue-600" />
            <span className="text-xl font-bold">
              {branding?.display_name || 'Customer Portal'}
            </span>
          </div>
          <CardTitle className="text-lg">
            {isRegister
              ? 'Create your NTOA account'
              : isAccept
                ? 'Activate your portal account'
                : isForgot
                  ? 'Reset your password'
                  : isReset
                    ? 'Set a new password'
                    : 'Sign in to your portal'}
          </CardTitle>
          <p className="text-sm text-gray-500">
            {isRegister
              ? 'Create your account to browse partner discounts and NTOA gear. Activate membership inside the portal any time to unlock redemption.'
              : isAccept
                ? 'Set a password to access your policies, claims, and certificates.'
                : isForgot
                  ? 'Enter your email and we\u2019ll send you a link to pick a new password.'
                  : isReset
                    ? 'Pick a new password. You\u2019ll be signed in automatically.'
                    : 'Use the email your producer set up.'}
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-3">
            {!isAccept && !isReset && (
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  data-testid={
                    isRegister
                      ? 'insured-register-email'
                      : isForgot
                        ? 'insured-forgot-email'
                        : 'insured-login-email'
                  }
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                />
              </div>
            )}
            {(isAccept || isRegister) && (
              <div>
                <Label htmlFor="name">Your Name</Label>
                <Input
                  id="name"
                  data-testid={isRegister ? 'insured-register-name' : 'insured-accept-name'}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={isRegister ? 'Jane Doe' : 'Optional'}
                  required={isRegister}
                />
              </div>
            )}
            {!isForgot && (
              <div>
                <Label htmlFor="password">
                  {isReset ? 'New password' : 'Password'}
                </Label>
                <Input
                  id="password"
                  data-testid={isReset ? 'insured-reset-password' : 'insured-password'}
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  minLength={8}
                  required
                />
                {(isAccept || isRegister || isReset) && (
                  <p className="text-xs text-gray-500 mt-1">
                    Minimum 8 characters.
                  </p>
                )}
              </div>
            )}
            {(isRegister || isForgot) && TURNSTILE_SITE_NTOA && (
              <div
                className="pt-1"
                data-testid={isRegister ? 'insured-register-turnstile' : 'insured-forgot-turnstile'}
              >
                <TurnstileWidget
                  siteKey={TURNSTILE_SITE_NTOA}
                  action={isRegister ? 'ntoa_signup' : 'ntoa_password_reset'}
                  onVerify={(t) => setTurnstileToken(t)}
                  onExpire={() => setTurnstileToken('')}
                  onError={() => setTurnstileToken('')}
                />
              </div>
            )}
            <Button
              type="submit"
              className="w-full"
              disabled={loading || (isReset && resetDone)}
              data-testid="insured-submit"
            >
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {isRegister
                ? 'Create Account'
                : isAccept
                  ? 'Create Account'
                  : isForgot
                    ? 'Send reset link'
                    : isReset
                      ? (resetDone ? 'Signed in — redirecting…' : 'Set new password')
                      : 'Sign In'}
            </Button>
            {!isAccept && !isRegister && !isForgot && !isReset && (
              <>
                <div className="relative my-2">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-white px-2 text-gray-400">or</span>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={requestMagicLink}
                  disabled={magicLoading || !form.email}
                  data-testid="insured-magic-link-btn"
                >
                  {magicLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Email me a sign-in link
                </Button>
                <div className="flex items-center justify-between pt-1">
                  <Link
                    to="/insured/forgot-password"
                    className="text-xs text-blue-600 hover:underline"
                    data-testid="insured-login-forgot-link"
                  >
                    Forgot password?
                  </Link>
                  <Link
                    to="/insured/register"
                    className="text-xs text-blue-600 hover:underline"
                    data-testid="insured-login-register-link"
                  >
                    Create an NTOA account
                  </Link>
                </div>
              </>
            )}
            {isRegister && (
              <p className="text-xs text-center text-gray-500">
                Already have an account?{' '}
                <Link
                  to="/insured/login"
                  className="text-blue-600 hover:underline"
                  data-testid="insured-register-login-link"
                >
                  Sign in
                </Link>
              </p>
            )}
            {(isForgot || isReset) && (
              <p className="text-xs text-center text-gray-500">
                <Link
                  to="/insured/login"
                  className="text-blue-600 hover:underline"
                  data-testid="insured-forgot-back-link"
                >
                  ← Back to sign in
                </Link>
              </p>
            )}
            <div className="text-center text-xs">
              <Link to="/" className="text-blue-600 hover:underline">
                ← Back to homepage
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default InsuredAuth;
